import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { usePopupPrefill } from "../../ai-chat/aiPopupGateway";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuUpload, LuSearch, LuHistory, LuHandCoins, LuRefreshCw } from "react-icons/lu";
import { LibraryEmptyState, LibraryGrid } from "./VideoLibraryComponents";
import VideoUploadSection from "./VideoUploadSection";
import { isFileTooLarge, fileTooLargeMessage } from "../../../../common/upload_limits.es6";
import AppModal from "../../common/modal/AppModal";
import InsufficientBalanceModal from "../../common/modal/InsufficientBalanceModal";
import {
  CLOSED_CREDIT_REFUSAL,
  creditRefusalState,
  isCreditRefusal,
} from "../../../utils/insufficientBalance";
import VideoAIGenerateTab, { CUSTOM_STYLE_ID } from "./VideoAIGenerateTab";
import VideoStoryboardPanel from "./VideoStoryboardPanel";
import VideoDirector from "./video-director/VideoDirector";
import HistoryTab from "../../add_image_modal/HistoryTab";
import { getToken } from "../../../../common/environment_config.es6";
import { fetchIdealStudioFiles, approveJob } from "../../../../common/server_utils.es6";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";
import { spinForSceneAdd } from "../../../utils/sceneAddSpinner";
import { isEligibleForAideaSmartElements } from "../../../../common/aideaSmartElements.es6";
import "./_addVideoModal.scss";
import { getContentLocale } from "../../../../common/content_language";

// Bir box'ın parent zincirini yürüyerek sahibi olan navItem id'sini bulur
// (box.parent doğrudan slide'a değil bir container'a işaret edebilir).
const resolveOwningNavId = (boxesById, navItemsById, startBoxId) => {
  let cursor = startBoxId;
  for (let i = 0; i < 32 && cursor != null; i++) {
    if (navItemsById && Object.prototype.hasOwnProperty.call(navItemsById, cursor)) return cursor;
    const b = boxesById && boxesById[cursor];
    if (!b) return cursor;
    cursor = b.parent;
  }
  return cursor;
};

// Providers that can also generate the still preview image. Video-only models
// (veo, veo-fast, seedance, ltx) can't, so they fall back to gemini.
const IMAGE_CAPABLE_PROVIDERS = ["grok"];
const resolvePreviewImageProvider = (videoProvider) =>
  IMAGE_CAPABLE_PROVIDERS.includes(videoProvider) ? videoProvider : "gemini";

// ─── Error localization helper ────────────────────────────────────────────────

function localizeErrorMessage(errorMsg) {
  if (!errorMsg || typeof errorMsg !== "string") return errorMsg;

  // Check for scene text validation error
  if (
    errorMsg.includes("text must be at least") &&
    errorMsg.includes("characters for meaningful narration")
  ) {
    return i18n.t("FileModal.APIProviders.VideoGeneration.SceneTextTooShort");
  }

  // Check for video prompt validation error
  if (
    errorMsg.includes("videoPrompt must be at least") &&
    errorMsg.includes("characters for meaningful video generation")
  ) {
    return i18n.t("FileModal.APIProviders.VideoGeneration.VideoPromptTooShort");
  }

  // Return original message if no match
  return errorMsg;
}

// ─── localStorage helpers ──────────────────────────────────────────────────────

const LS_JOBS_INDEX = "video-gen-jobs";

function readJobsIndex() {
  try {
    return JSON.parse(localStorage.getItem(LS_JOBS_INDEX)) || [];
  } catch {
    return [];
  }
}

function writeJobsIndex(jobs) {
  try {
    localStorage.setItem(LS_JOBS_INDEX, JSON.stringify(jobs));
  } catch {
    /* ignore */
  }
}

function jobKey(jobId) {
  return `video-gen-job-${jobId}`;
}

function saveJob(data) {
  try {
    localStorage.setItem(jobKey(data.jobId), JSON.stringify(data));
  } catch {
    /* ignore */
  }
  const index = readJobsIndex();
  const existing = index.findIndex((j) => j.jobId === data.jobId);
  const summary = {
    jobId: data.jobId,
    status: data.status,
    progress: data.progress,
    narrationText: data.narrationText,
    startedAt: data.startedAt,
  };
  if (existing >= 0) index[existing] = summary;
  else index.push(summary);
  writeJobsIndex(index);
}

function loadJob(jobId) {
  try {
    const raw = localStorage.getItem(jobKey(jobId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function removeJob(jobId) {
  try {
    localStorage.removeItem(jobKey(jobId));
  } catch {
    /* ignore */
  }
  writeJobsIndex(readJobsIndex().filter((j) => j.jobId !== jobId));
}

// ─── storyboard localStorage helpers ───────────────────────────────────────────
// Her eğitim kendi storyboard state'ine sahip olsun diye key trainingId'ye göre türetilir.

const LS_STORYBOARD_PREFIX = "video-gen-storyboard";

function storyboardKey(trainingId) {
  return `${LS_STORYBOARD_PREFIX}:${trainingId}`;
}

function saveStoryboardState(trainingId, data) {
  if (!trainingId) return;
  try {
    // savedAt: TTL tabanlı temizlik için. Aktif storyboard her kaydetmede
    // tazelendiğinden TTL içinde kalır; eskiyenler purge tarafından silinir.
    localStorage.setItem(
      storyboardKey(trainingId),
      JSON.stringify({ ...data, savedAt: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

function loadStoryboardState(trainingId) {
  if (!trainingId) return null;
  try {
    const raw = localStorage.getItem(storyboardKey(trainingId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function removeStoryboardState(trainingId) {
  if (!trainingId) return;
  try {
    localStorage.removeItem(storyboardKey(trainingId));
  } catch {
    /* ignore */
  }
}

// Modal içinden başlatılan storyboard job'ları widget'ta "Onay Bekliyor" kartı
// olarak da görünür; sonuçları modalın içinde tüketildiği için approve etmezsek
// kart kuyrukta takılı kalır. Bu job id'leri storyboard yanıtında dönmediğinden
// başlatma anında storyboardId'ye bağlı saklanır (reload'a dayanıklı olsun diye LS):
//   - "init": staged-video-init (modal formu) → sahneler hazır olunca approve.
//   - "all":  staged-video-generate-all ("Tümünü üret") → tüm sahneler bitince approve.
const LS_STORYBOARD_JOB_PREFIX = "video-gen-storyboard-job";

function rememberStoryboardJob(kind, storyboardId, jobId) {
  if (!storyboardId || !jobId) return;
  try {
    localStorage.setItem(`${LS_STORYBOARD_JOB_PREFIX}-${kind}:${storyboardId}`, String(jobId));
  } catch {
    /* ignore */
  }
}

function approveStoryboardJob(kind, storyboardId) {
  if (!storyboardId) return;
  try {
    const key = `${LS_STORYBOARD_JOB_PREFIX}-${kind}:${storyboardId}`;
    const jobId = localStorage.getItem(key);
    if (!jobId) return;
    localStorage.removeItem(key);
    approveJob(jobId);
  } catch {
    /* ignore */
  }
}

// Storyboard kayıtları kalıcı; sahne/eğitim sayısı arttıkça localStorage'da
// sınırsız birikiyordu. Bu fonksiyon TTL'i geçen kayıtları VE zaman damgası
// olmayan eski (legacy) kayıtları siler. Aktif storyboard'lar savedAt ile
// sürekli tazelendiğinden TTL içinde silinmez.
const STORYBOARD_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 gün

function purgeExpiredStoryboards(now = Date.now()) {
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${LS_STORYBOARD_PREFIX}:`)) continue;
      let savedAt = null;
      try {
        savedAt = JSON.parse(localStorage.getItem(key))?.savedAt ?? null;
      } catch {
        /* bozuk JSON → sil */
      }
      if (typeof savedAt !== "number" || now - savedAt > STORYBOARD_TTL_MS) {
        stale.push(key);
      }
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

// ─── constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 8000;
const MAX_POLL_ATTEMPTS = 200;
// Director tab with no specific project lands on the scene's latest project (stable identity: VideoDirector effects key on it).
const LATEST_PROJECT = { latest: true };

const getLanguageOptions = () => [
  { value: "tr-TR", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.tr-TR") },
  { value: "en-US", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.en-US") },
  { value: "es-ES", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.es-ES") },
  { value: "fr-FR", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.fr-FR") },
  { value: "de-DE", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.de-DE") },
  { value: "ar-SA", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.ar-SA") },
  { value: "ja-JP", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.ja-JP") },
  { value: "pt-BR", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.pt-BR") },
  { value: "az-AZ", label: i18n.t("FileModal.APIProviders.VideoGeneration.Languages.az-AZ") },
];

const PROVIDER_OPTIONS = [
  { value: "grok", label: "Grok" },
  { value: "veo-fast", label: "Omni (720p)" },
  { value: "veo-lite", label: "Omni (720p)" },
  { value: "veo", label: "Omni" },
];

export const statusColor = {
  processing: "#17a2b8",
  pending: "#f59e0b",
  completed: "#22c55e",
  failed: "#ef4444",
};

// ─── VideoModal ────────────────────────────────────────────────────────────────

const VideoModal = ({
  open,
  onClose,
  title,
  narrationText: narrationTextProp = "",
  audioUrl,
  sceneNarrationText,
  trainingId,
  idealStudioTrainingId,
  onVideoReady,
  sceneIndex,
  onBoxAdded,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  boxSelected,
  pluginToolbars,
  uploadFunction,
  onVideoSelected,
  importUrl,
  importPromptProfile,
  direct,
  projectId,
  onNavItemSelected,
  navId,
}) => {
  const apiUrl = process.env.MW_API_URL || window.ideaLibraryRootUrl || "";

  const [activeTab, setActiveTab] = useState("ai");
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadUploading, setUploadUploading] = useState(false);
  const [uploadUploaded, setUploadUploaded] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  // Free-text status shown once the upload's file-transfer leg finishes and
  // the backend job (transcode/finalize) is running — e.g. "Sunucuda
  // işleniyor..." — as opposed to uploadPercentage's numeric "%NN" progress.
  const [uploadStatusMessage, setUploadStatusMessage] = useState("");
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  // ── form state ──
  const [narrationText, setNarrationText] = useState(narrationTextProp);
  const [contentLanguage, setContentLanguage] = useState(() => getContentLocale());
  const [provider, setProvider] = useState("veo");
  const [videoStyle, setVideoStyle] = useState("");
  const [customStyleDesc, setCustomStyleDesc] = useState("");
  const [videoDuration, setVideoDuration] = useState("");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [durationMode, setDurationMode] = useState("auto");

  // ── video styles state ──
  const [videoStyles, setVideoStyles] = useState([]);
  const [stylesLoading, setStylesLoading] = useState(false);
  // Live mirror so applyVideoIntent (stable []-dep callback) can resolve the
  // agent's picked style name/key against the fetched catalog without a stale
  // closure or re-subscribing the prefill listener.
  const videoStylesRef = useRef(videoStyles);
  videoStylesRef.current = videoStyles;

  // ── library state ──
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [selectedLibraryId, setSelectedLibraryId] = useState(null);
  const [hoveredLibraryId, setHoveredLibraryId] = useState(null);

  // ── job state ──
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobResult, setJobResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [jobsIndex, setJobsIndex] = useState(() => readJobsIndex());

  const pollTimerRef = useRef(null);
  const activeJobIdRef = useRef(null);

  // ── prompt mode ──
  const [promptMode, setPromptMode] = useState("write");
  const [scenes, setScenes] = useState([{ id: 1, text: "" }]);
  const [enrichTexts, setEnrichTexts] = useState(false);

  // Chat agent → "createVideo": prefill the script + any provider/aspect/duration
  // hints. The user still picks the style + model. Best-effort.
  const applyVideoIntent = useCallback(({ prompt, settings }) => {
    if (typeof prompt === "string" && prompt.trim()) {
      setActiveTab("ai");
      setPromptMode("write");
      // The visible "Write Prompt" textarea is bound to scenes[activeScene].text,
      // so the agent's script must go there. (narrationText only shows in the
      // "From Narration" tab, and switching to "write" clears it.) Set both so
      // either tab shows the script.
      setScenes([{ id: 1, text: prompt }]);
      setNarrationText(prompt);
    }
    const s = settings || {};
    if (s.provider) setProvider(s.provider);
    if (s.language) setContentLanguage(s.language);
    if (s.aspectRatio) setAspectRatio(s.aspectRatio);
    if (typeof s.durationSeconds === "number") {
      setVideoDuration(String(s.durationSeconds));
      setDurationMode("custom");
    }
    // Agent pre-picked a style: the backend sends a styleKey, but resolve by
    // name too (against the live catalog) so a name still selects the card.
    if (s.style) {
      const want = String(s.style).toLowerCase();
      const list = videoStylesRef.current || [];
      const match = list.find(
        (v) => v.styleKey?.toLowerCase() === want || v.name?.toLowerCase() === want
      );
      setVideoStyle(match ? match.styleKey : s.style);
    }
  }, []);
  usePopupPrefill("video", applyVideoIntent);

  // ── storyboard training+scene scope ──
  // Storyboard state'i aktif eğitim VE aktif sahne altında localStorage'a yazılır,
  // böylece aynı eğitim içinde her sahnenin kendi storyboard'u olur.
  // Scene identity for localStorage scoping. `navId` (passed per-scene by
  // SceneAudioPanel) is authoritative; fall back to navItemSelected for callers
  // that don't pass it (EditorModals/EditorApp). Without this, scenes opened from
  // SceneAudioPanel (which doesn't pass navItemSelected) all collapse to the same
  // training-only key and overwrite each other's storyboard.
  const storyboardSceneId = useMemo(() => {
    if (containedViewSelected && containedViews && containedViews[containedViewSelected]) {
      return containedViewSelected;
    }
    return navId || navItemSelected || null;
  }, [containedViewSelected, containedViews, navId, navItemSelected]);

  // Aktif sahnedeki görseller (Shapes / HotspotImages plugin box'ları). Referans
  // görsel (image-to-video) olarak "sahnedeki görsellerden seç" için kullanılır.
  const sceneImages = useMemo(() => {
    if (!pluginToolbars || !boxes) return [];
    const seen = new Set();
    const out = [];
    Object.keys(pluginToolbars).forEach((boxId) => {
      const tb = pluginToolbars[boxId];
      if (!isEligibleForAideaSmartElements(tb)) return;
      const url = tb.state.url;
      // webp'ler hariç — sadece image (jpg/png/...) gelsin.
      if (typeof url === "string" && url.split("?")[0].toLowerCase().endsWith(".webp")) return;
      if (seen.has(url)) return;
      const box = boxes[boxId];
      // storyboardSceneId varsa yalnızca o sahneye ait görselleri göster.
      if (storyboardSceneId && box) {
        const owning = resolveOwningNavId(boxes, navItems, box.parent);
        if (owning !== storyboardSceneId) return;
      }
      seen.add(url);
      out.push({ url, boxId });
    });
    return out;
  }, [pluginToolbars, boxes, navItems, storyboardSceneId]);

  const storyboardTrainingId = useMemo(() => {
    const tid =
      trainingId ||
      (typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.search).get("id"));
    if (!tid) return null;
    return storyboardSceneId ? `${tid}:${storyboardSceneId}` : tid;
  }, [trainingId, storyboardSceneId]);

  // storyboardTrainingId aktif sahneye (navItemSelected) bağlı ve üretim sırasında
  // JobWidgetHost sahneler arası otomatik geçiş yaptıkça değişir. localStorage'a
  // yazarken bu CANLI değeri kullanmak, tek bir storyboard'un gezilen her sahnenin
  // key'ine kopyalanmasına yol açıyordu (aynı job birden fazla sahnede görünür).
  // Bu yüzden persist key'i storyboard oluşturulduğu/yüklendiği ANDA sabitlenir
  // (storyboardKeyRef) ve tüm yazımlar bu sabit key'e gider. Load/remove (okuma)
  // yolları aktif sahneye göre kalır — her sahnenin kendi storyboard'unu göstermek
  // için gerekli. currentTrainingKeyRef ise event handler'ların stale closure
  // olmadan güncel key'i okuyabilmesi için canlı bir aynadır.
  const storyboardKeyRef = useRef(null);
  const currentTrainingKeyRef = useRef(null);
  currentTrainingKeyRef.current = storyboardTrainingId;

  // ── storyboard state ──
  const [view, setView] = useState("form"); // "form" | "storyboard"
  const [storyboardId, setStoryboardId] = useState(null);
  const [storyboardScenes, setStoryboardScenes] = useState([]);
  const [sceneJobs, setSceneJobs] = useState({});
  const [isStoryboardCreating, setIsStoryboardCreating] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [editPromptValue, setEditPromptValue] = useState("");
  const [isSavingScene, setIsSavingScene] = useState(false);
  const [styleReferenceFile, setStyleReferenceFile] = useState(null);
  const [textOverlays, setTextOverlays] = useState([]);
  const [awaitingNextScene, setAwaitingNextScene] = useState(false);
  const [allScenesGenerated, setAllScenesGenerated] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);

  // ── Kredi tahmini (storyboard footer) ──
  const [creditInfo, setCreditInfo] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditPopoverOpen, setCreditPopoverOpen] = useState(false);
  const creditBtnRef = useRef(null);
  const [insufficientBalance, setInsufficientBalance] = useState(CLOSED_CREDIT_REFUSAL);

  // Helper: detect a credit refusal (org balance or team budget) in any API
  // error body and open the popup. Returns true when handled so callers can
  // short-circuit normal error flow.
  const handleInsufficientBalance = useCallback((errData) => {
    if (!isCreditRefusal(errData)) return false;
    setInsufficientBalance(creditRefusalState(errData));
    return true;
  }, []);

  const handleStoryboardNotFound = useCallback(() => {
    console.log("[VideoModal] Storyboard not found - clearing localStorage");
    // Yüklü storyboard'un sabit key'ini sil (deps [] olduğu için storyboardTrainingId
    // stale kalırdı; pinned key her zaman doğru sahneyi gösterir).
    removeStoryboardState(storyboardKeyRef.current || currentTrainingKeyRef.current);
    storyboardKeyRef.current = null;
    setStoryboardId(null);
    setStoryboardScenes([]);
    setSceneJobs({});
    setIsStoryboardCreating(false);
    setView("form");
  }, []);

  // Bekleyen (henüz üretilmemiş) sahne sayısı — kredi butonunu göstermek için.
  const pendingScenesCount = useMemo(
    () =>
      (storyboardScenes || []).filter((sc) => {
        const status = sceneJobs[sc.index]?.status || sc.status || "pending";
        return status === "pending";
      }).length,
    [storyboardScenes, sceneJobs]
  );

  // Tıklayınca video-credit (kalan sahne) + image-credit (önizleme görseli)
  // isteklerini AYNI ANDA atar; ikisi de dönünce popover'da iki maliyet satırı gösterilir.
  const handleCreditCheck = useCallback(async () => {
    if (creditPopoverOpen) {
      setCreditPopoverOpen(false);
      return;
    }
    if (!storyboardId) return;
    setCreditLoading(true);
    setCreditInfo(null);

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    };

    const fetchCredit = async (endpoint, body) => {
      const res = await fetch(`${apiUrl}${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const error = new Error(`HTTP ${res.status}`);
        error.status = res.status;
        error.data = errData;
        throw error;
      }
      return res.json();
    };

    try {
      const [videoData, imageData] = await Promise.all([
        fetchCredit("api/course-video/generate-staged/continue/credit", {
          storyboardId,
          mode: "next",
        }),
        fetchCredit("api/course-video/preview-image/credit", {
          provider: resolvePreviewImageProvider(provider),
          imageCount: 1,
        }),
      ]);

      const availableCredits =
        Number(videoData.availableCredits) || Number(imageData.availableCredits) || 0;
      const videoCost = Number(videoData.requiredCredits) || 0;
      const imageCost = Number(imageData.requiredCredits) || 0;
      const totalRequired = Number((videoCost + imageCost).toFixed(4));

      setCreditInfo({
        videoCost,
        imageCost,
        totalRequired,
        availableCredits,
        hasSufficientBalance: availableCredits >= totalRequired,
      });
      setCreditPopoverOpen(true);
    } catch (err) {
      if (err?.status === 404) {
        handleStoryboardNotFound?.();
        return;
      }
      if (handleInsufficientBalance?.(err?.data)) return;
      console.error("[VideoModal] Credit check error:", err);
    } finally {
      setCreditLoading(false);
    }
  }, [
    creditPopoverOpen,
    storyboardId,
    apiUrl,
    provider,
    handleStoryboardNotFound,
    handleInsufficientBalance,
  ]);

  // Popover dışına tıklayınca kapat
  useEffect(() => {
    if (!creditPopoverOpen) return undefined;
    const onClickOutside = (e) => {
      if (creditBtnRef.current && !creditBtnRef.current.contains(e.target)) {
        setCreditPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [creditPopoverOpen]);

  const storyboardPollRef = useRef(null);
  const sceneJobPollRefs = useRef({});
  const startedSceneJobsRef = useRef(new Set());
  const storyboardNavIdRef = useRef(null);
  const storyboardAbortControllerRef = useRef(null);
  const storyboardGenRef = useRef(0); // her yeni storyboard'da artar; eski iterasyonlar durur
  const handleAddToSceneRef = useRef(null);

  // Senkron LS yazımı için snapshot ref. Effect timing yarışından kaçınmak için
  // poll callback'lerinden doğrudan LS'e yazarken bu ref kullanılır.
  const storyboardSnapshotRef = useRef({
    storyboardId: null,
    storyboardScenes: [],
    sceneJobs: {},
    awaitingNextScene: false,
    allScenesGenerated: false,
  });

  // staged-video-init handler bir storyboard kurduğunda set edilir. Restore-job
  // effect'i bunu görünce storyboard state'ini sıfırlamayı atlar; aksi halde
  // handler'ın setView("storyboard")'ı sonradan üzerine yazılıp form'a döner.
  const pendingStoryboardRef = useRef(null);

  const handleAddToScene = useCallback(
    async (url, name, targetNavId, extra) => {
      if (onVideoSelected && url) {
        onVideoSelected(url, extra);
        onClose?.();
        return;
      }
      if (!onBoxAdded || !url) {
        return;
      }

      const IdealStudio = require("../../../../core/editor/main").default;
      const { isSlide, isContainedView } = require("../../../../common/utils");
      const { ID_PREFIX_BOX } = require("../../../../common/constants");
      const { randomPositionGenerator } = require("../../clipboard/clipboard.utils");
      const { createBox } = require("../../../../common/common_tools");

      const pluginName = "EnrichedPlayer";
      const plugin = IdealStudio.Plugins.get(pluginName);
      if (!plugin) {
        return;
      }

      // targetNavId varsa o sahneye ekle, yoksa seçili sahneye ekle
      const resolvedNavId =
        targetNavId ||
        (containedViewSelected && containedViews[containedViewSelected]
          ? containedViewSelected
          : navItemSelected);

      const navItem = navItems[resolvedNavId];

      if (!navItem) {
        return;
      }

      const cv =
        !targetNavId &&
        containedViewSelected &&
        containedViews[containedViewSelected] &&
        isContainedView(containedViewSelected);
      const cvslide = cv && isSlide(navItem.type);

      const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
      const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

      // Pozisyon + id await gerektirmez → hemen üret ve spinner'ı TIKLAMA ANINDA
      // göster; kutu render olunca üstüne geçip video karesi gelince kapanır.
      const position = {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      };
      const boxId = ID_PREFIX_BOX + Date.now();
      spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: cv });

      const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(url, "video");

      const initialParams = {
        parent,
        container: 0,
        name: pluginName,
        position,
        id: boxId,
        page: cv ? containedViewSelected : navItem.id || resolvedNavId || 0,
        url,
        width,
        height,
        widthUnit,
        heightUnit,
        // Video Director: createBox copies it into plugin state so "Replace File" can reopen the project.
        ...(extra?.videoProjectId ? { videoProjectId: extra.videoProjectId } : {}),
      };

      createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);

      // Hedef sahne farklıysa o sahneye geç (tts-avatar ADD_TO_SCENE davranışıyla aynı)
      if (targetNavId && targetNavId !== navItemSelected && onNavItemSelected) {
        setTimeout(() => onNavItemSelected(targetNavId), 100);
      }

      if (!targetNavId) onClose?.();
    },
    [
      onBoxAdded,
      boxes,
      navItems,
      navItemSelected,
      containedViews,
      containedViewSelected,
      boxSelected,
      pluginToolbars,
      onClose,
      onNavItemSelected,
    ]
  );

  // Storyboard panelindeki sahne kartının "Sahneye ekle"si: video modal içinden
  // eklendiğinde ilgili staged-video-generation job'u widget'ta "Onay Bekliyor"
  // olarak kalmasın diye approve edilir (AIImageGenerateTab ile aynı sözleşme).
  const handleAddStoryboardSceneToScene = useCallback(
    (url, name, sceneJobId) => {
      handleAddToScene(url, name);
      if (sceneJobId) approveJob(sceneJobId);
    },
    [handleAddToScene]
  );

  // handleAddToSceneRef: pollSceneJob'dan stale closure olmadan çağırabilmek için
  useEffect(() => {
    handleAddToSceneRef.current = handleAddToScene;
  });

  // Reset promptMode to "write" when modal opens
  useEffect(() => {
    if (open) {
      setPromptMode("write");
      setNarrationText("");
    }
  }, [open]);

  // Sync narrationText when prop changes (different audio row clicked)
  useEffect(() => {
    setNarrationText(narrationTextProp);
  }, [narrationTextProp]);

  // Fetch video styles from API
  useEffect(() => {
    const fetchVideoStyles = async () => {
      setStylesLoading(true);
      try {
        const response = await fetch(`${apiUrl}api/video-prompt-library/active`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (response.ok) {
          const result = await response.json();
          setVideoStyles(result?.data || []);
        }
      } catch (err) {
        console.error("[VideoModal] Failed to fetch video styles:", err);
      } finally {
        setStylesLoading(false);
      }
    };
    if (open) {
      fetchVideoStyles();
    }
  }, [open, apiUrl]);

  useEffect(() => {
    if (!open || activeTab !== "library") return;
    let isCancelled = false;

    const loadLibrary = async () => {
      setLibraryLoading(true);
      setLibraryError("");
      try {
        const response = await fetchIdealStudioFiles({
          search: librarySearch || undefined,
          limit: 50,
          offset: 0,
          fileType: "video",
        });

        if (!isCancelled && response && Array.isArray(response.files)) {
          const mapped = response.files.map((file) => ({
            id: file.id,
            name: file.fileName || file.filename || "Untitled",
            url: file.fileVirtualPath,
            raw: file,
          }));
          setLibraryFiles(mapped);
        }
      } catch (err) {
        if (!isCancelled) {
          setLibraryError(i18n.t("FileModal.APIProviders.VideoGeneration.LibraryLoadError"));
          setLibraryFiles([]);
        }
      } finally {
        if (!isCancelled) {
          setLibraryLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(loadLibrary, 250);
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [open, activeTab, librarySearch]);

  const refreshJobsIndex = useCallback(() => {
    setJobsIndex(readJobsIndex());
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    async (targetJobId, attempt = 0) => {
      if (!targetJobId || attempt >= MAX_POLL_ATTEMPTS) return;
      try {
        const response = await fetch(`${apiUrl}api/course-video/job/${targetJobId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });

        if (response.status === 404) {
          removeJob(targetJobId);
          if (activeJobIdRef.current === targetJobId) {
            setJobId(null);
            setJobStatus(null);
            setJobProgress(0);
          }
          refreshJobsIndex();
          return;
        }

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        const status = data?.status || "unknown";
        const progress = data?.progress ?? 0;
        const existing = loadJob(targetJobId) || {};
        const updatedJob = {
          ...existing,
          jobId: targetJobId,
          status,
          progress,
          result: status === "completed" ? data.result : existing.result || null,
        };
        saveJob(updatedJob);
        refreshJobsIndex();

        if (activeJobIdRef.current === targetJobId) {
          setJobStatus(status);
          setJobProgress(progress);
          if (status === "completed") setJobResult(data.result);
        }

        if (status === "completed" || status === "failed") return;

        pollTimerRef.current = setTimeout(
          () => pollJob(targetJobId, attempt + 1),
          POLL_INTERVAL_MS
        );
      } catch (err) {
        console.error("[VideoModal] Poll error:", err);
        pollTimerRef.current = setTimeout(
          () => pollJob(targetJobId, attempt + 1),
          POLL_INTERVAL_MS * 2
        );
      }
    },
    [apiUrl, refreshJobsIndex]
  );

  // Restore job when modal opens
  useEffect(() => {
    if (!open) return;

    // staged-video-init handler az önce bir storyboard kurduysa, storyboard
    // state'ini sıfırlama — handler'ın setView("storyboard"), setStoryboardId(NEW)
    // gibi çağrılarını override edip kullanıcıyı form view'a atmamak için.
    // Bunun yerine Load-from-LS effect'i devralır.
    const skipStoryboardReset = !!pendingStoryboardRef.current;

    // Always stop any lingering poll and reset legacy job UI
    stopPolling();
    activeJobIdRef.current = null;
    setJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setJobResult(null);
    setError(null);
    setStyleReferenceFile(null);
    setTextOverlays([]);

    if (!skipStoryboardReset) {
      stopStoryboardPolling();
      setView("form");
      setStoryboardId(null);
      setStoryboardScenes([]);
      setSceneJobs({});
      setIsStoryboardCreating(false);
      setEditingScene(null);
      setEditPromptValue("");
      setAwaitingNextScene(false);
      setAllScenesGenerated(false);
      startedSceneJobsRef.current = new Set();
    }

    refreshJobsIndex();
    const allJobs = readJobsIndex();
    // Only restore a job that belongs to THIS narration (strict match required)
    const candidate = narrationTextProp
      ? allJobs
          .filter((j) => j.narrationText === narrationTextProp)
          .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0))[0]
      : null;

    if (candidate) {
      const full = loadJob(candidate.jobId);
      if (full) {
        activeJobIdRef.current = full.jobId;
        setJobId(full.jobId);
        setJobStatus(full.status);
        setJobProgress(full.progress || 0);
        setJobResult(full.result || null);
        if (full.status !== "completed" && full.status !== "failed") {
          pollJob(full.jobId, 0);
        }
      }
    }
  }, [open, narrationTextProp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── Storyboard helpers ─────────────────────────────────────────────────────────

  const stopStoryboardPolling = useCallback(() => {
    storyboardGenRef.current += 1; // eski iterasyonları geçersiz kıl
    if (storyboardPollRef.current) {
      clearTimeout(storyboardPollRef.current);
      storyboardPollRef.current = null;
    }
    Object.values(sceneJobPollRefs.current).forEach((t) => clearTimeout(t));
    sceneJobPollRefs.current = {};
    storyboardAbortControllerRef.current?.abort();
    storyboardAbortControllerRef.current = null;
  }, []);

  useEffect(() => () => stopStoryboardPolling(), [stopStoryboardPolling]);

  // Oturum başında localStorage'da biriken eski/expired storyboard kayıtlarını
  // temizle (TTL + legacy). Bir kez çalışır; aktif kayıtlar savedAt ile korunur.
  useEffect(() => {
    purgeExpiredStoryboards();
  }, []);

  // ── staged-video-init job approval → polling başlat ──────────────────────────
  useEffect(() => {
    const handleStagedVideoApproved = (e) => {
      const { storyboardId: newStoryboardId, job } = e.detail || {};
      if (!newStoryboardId) return;

      // job'dan navId varsa kaydet (job.navId genellikle null gelir,
      // pollStoryboard'dan storyboard.navId okunarak güncellenecek)
      if (job?.navId) storyboardNavIdRef.current = job.navId;

      // Staged akışta storyboard formu doldurulmadan kurulur; provider/aspectRatio
      // job.metadata'da varsa state'e yaz ki preview-image doğru provider'ı kullansın
      // ve save-effect bunları LS'e persist etsin.
      const stagedProvider = job?.metadata?.provider || job?.provider;
      const stagedAspectRatio = job?.metadata?.aspectRatio || job?.aspectRatio;
      if (stagedProvider) setProvider(stagedProvider);
      if (stagedAspectRatio) setAspectRatio(stagedAspectRatio);

      // Listener stale closure tutmasın diye canlı key'i ref'ten oku.
      const trainingKey = currentTrainingKeyRef.current;

      stopPolling();
      stopStoryboardPolling(); // abort + timeout temizlik
      removeStoryboardState(trainingKey);
      // Bu storyboard'u onaylandığı sahnenin key'ine sabitle — üretim sırasında
      // JobWidgetHost sahne değiştirse bile persist hep bu key'e yazsın.
      storyboardKeyRef.current = trainingKey;
      // Yeni storyboard için taze AbortController
      storyboardAbortControllerRef.current = new AbortController();

      // Modal açılınca "Restore job" effect view'ı "form"'a sıfırlar.
      // Storyboard restore effect (open değişince çalışan) bunu geçersiz kılsın diye
      // localStorage'a minimal state kaydet — modal açılmadan ÖNCE.
      saveStoryboardState(trainingKey, {
        storyboardId: newStoryboardId,
        scenes: [],
        sceneJobs: {},
        awaitingNextScene: false,
        allScenesGenerated: false,
        provider: stagedProvider,
        aspectRatio: stagedAspectRatio,
      });

      // Eski storyboard'un snapshot ref'i temizle — aksi halde persistStoryboardNow
      // eski sahne/jobs ile yeni storyboardId'yi karıştırıp LS'e bozuk veri yazabilir.
      storyboardSnapshotRef.current = {
        storyboardId: newStoryboardId,
        storyboardScenes: [],
        sceneJobs: {},
        awaitingNextScene: false,
        allScenesGenerated: false,
        provider: stagedProvider,
        aspectRatio: stagedAspectRatio,
      };

      // Restore-job effect bu flag'i görüp storyboard state'ini sıfırlamayacak.
      pendingStoryboardRef.current = newStoryboardId;

      setStoryboardId(newStoryboardId);
      setStoryboardScenes([]);
      setSceneJobs({});
      setIsStoryboardCreating(true);
      setAwaitingNextScene(false);
      setAllScenesGenerated(false);
      setView("storyboard");
      startedSceneJobsRef.current = new Set();

      // Modal kapalıysa: openVideoModal event'i `open=false→true` yapacak ve
      //   Load-from-LS effect taze pollStoryboard'u 0ms gecikmeyle başlatacak.
      // Modal açıksa: open değişmediği için Load-from-LS effect fire ETMEZ;
      //   o yüzden polling'i burada explicit başlatmak zorundayız.
      const wasModalAlreadyOpen = open;

      // Modalı aç
      window.dispatchEvent(new CustomEvent("openVideoModal"));

      if (wasModalAlreadyOpen) {
        const gen = storyboardGenRef.current;
        storyboardPollRef.current = setTimeout(() => pollStoryboard(newStoryboardId, 0, gen), 0);
      }
      // Modal kapalı senaryosunda Load-from-LS effect halleder — burada timer
      // schedule ETMEYELİM; aksi halde Restore-job'un stopStoryboardPolling
      // çağrısı gen'i bumplar ve burada captured gen stale kalır.
    };

    window.addEventListener("stagedVideoJobApproved", handleStagedVideoApproved);
    return () => window.removeEventListener("stagedVideoJobApproved", handleStagedVideoApproved);
  }, [open, stopPolling, stopStoryboardPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load storyboard from localStorage on mount / scene change ─────────────────
  // Modal her açıldığında VE aktif sahne değiştiğinde fire eder; böylece
  // sahneler arası gezinince o sahnenin kendi storyboard'u açılır.
  useEffect(() => {
    if (!open) return;
    const saved = loadStoryboardState(storyboardTrainingId);
    if (saved && saved.storyboardId) {
      // staged-video-init handler bunu set ettiyse, Restore-job effect'i atlama
      // tamamlandı; flag'i temizle ki sonraki açılışlarda yanlış davranmasın.
      pendingStoryboardRef.current = null;

      // Persist key'i yüklendiği sahnenin key'ine sabitle.
      storyboardKeyRef.current = storyboardTrainingId;

      setStoryboardId(saved.storyboardId);
      setStoryboardScenes(saved.scenes || []);
      setSceneJobs(saved.sceneJobs || {});
      // provider/aspectRatio'yu geri yükle — preview-image üretimi ve video
      // önizleme boyutu bunlara bağlı. Eski (provider'sız) kayıtlarda mevcut
      // değeri koru.
      if (saved.provider) setProvider(saved.provider);
      if (saved.aspectRatio) setAspectRatio(saved.aspectRatio);
      setView("storyboard");

      // Sahneler henüz gelmemişse yükleniyor spinnerı göster
      const scenes = saved.scenes || [];
      setIsStoryboardCreating(scenes.length === 0);
      const jobs = saved.sceneJobs || {};
      // scenes boş ise vacuously-true olur — onu "henüz başlamamış" sayıp
      // allScenesGenerated=false bırakmak gerekiyor; aksi halde GenerateAllScenes
      // butonu hiç görünmüyor ve UI bozuk durum gösteriyor.
      const allDone =
        scenes.length > 0 && scenes.every((s) => s.status === "completed" || s.status === "failed");

      if (allDone) {
        setAllScenesGenerated(true);
        setAwaitingNextScene(false);
      } else {
        const hasCompleted = scenes.some((s) => s.status === "completed");
        const hasPending = scenes.some(
          (s) => s.status === "pending" || s.status === "initializing"
        );
        const anyGenerating = scenes.some(
          (s) => s.status === "generating" || s.status === "processing"
        );
        const anyJobProcessing = Object.values(jobs).some(
          (job) => job.status === "processing" || job.status === "generating"
        );

        // Show continue buttons if applicable
        if (hasCompleted && hasPending && !anyGenerating && !anyJobProcessing) {
          setAwaitingNextScene(true);
        } else {
          setAwaitingNextScene(saved.awaitingNextScene || false);
        }
        setAllScenesGenerated(false);
      }

      // Snapshot ref'i de doldur ki sonraki persistStoryboardNow çağrıları stale olmasın
      storyboardSnapshotRef.current = {
        storyboardId: saved.storyboardId,
        storyboardScenes: saved.scenes || [],
        sceneJobs: saved.sceneJobs || {},
        awaitingNextScene: saved.awaitingNextScene || false,
        allScenesGenerated: !!saved.allScenesGenerated,
        provider: saved.provider,
        aspectRatio: saved.aspectRatio,
      };

      // Resume polling if needed
      if (saved.storyboardId) {
        // İlk fetch'i hemen yap; sonraki polls zaten POLL_INTERVAL_MS ile devam eder
        storyboardPollRef.current = setTimeout(
          () => pollStoryboard(saved.storyboardId, 0, storyboardGenRef.current),
          0
        );
      }
    } else {
      // Aktif sahne için kayıtlı storyboard yok → state'i form görünümüne sıfırla.
      // Önceki sahnenin storyboard'u UI'da kalmasın.
      stopStoryboardPolling();
      startedSceneJobsRef.current = new Set();
      pendingStoryboardRef.current = null;
      storyboardKeyRef.current = null;
      setStoryboardId(null);
      setStoryboardScenes([]);
      setSceneJobs({});
      setIsStoryboardCreating(false);
      setAwaitingNextScene(false);
      setAllScenesGenerated(false);
      setView("form");
      storyboardSnapshotRef.current = {
        storyboardId: null,
        storyboardScenes: [],
        sceneJobs: {},
        awaitingNextScene: false,
        allScenesGenerated: false,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storyboardTrainingId]);

  // Video Director: "Replace File" (onVideoSelected) on a Director-made video
  // reopens its project on the Director tab; "Edit in Director" (importUrl) imports the box's video there. Declared after the storyboard
  // restore effect above so this setView("form") wins the same commit.
  const directorProjectId =
    projectId ||
    (onVideoSelected && !importUrl && pluginToolbars?.[boxSelected]?.state?.videoProjectId) ||
    null;
  useEffect(() => {
    if (!open || !(directorProjectId || importUrl || direct)) return;
    setView("form");
    setActiveTab("director");
  }, [open, directorProjectId, importUrl, direct]);

  // ── Save storyboard to localStorage whenever it changes ───────────────────────
  useEffect(() => {
    storyboardSnapshotRef.current = {
      storyboardId,
      storyboardScenes,
      sceneJobs,
      awaitingNextScene,
      allScenesGenerated,
      // provider/aspectRatio storyboard oluşturulurken seçilir ve preview-image
      // üretimini (resolvePreviewImageProvider) belirler. Sayfa yenilenince form
      // state'i default'a döndüğü için bunları da persist edip restore ediyoruz;
      // aksi halde grok ile üretilen storyboard reload sonrası gemini'ye düşüyordu.
      provider,
      aspectRatio,
    };
    // Canlı storyboardTrainingId yerine sabitlenmiş key'e yaz: üretim sırasında
    // sahne değişse bile storyboard hep kendi key'ine kaydedilsin, başka sahnelere
    // kopyalanmasın.
    const persistKey = storyboardKeyRef.current;
    if (persistKey && storyboardId && storyboardScenes.length > 0) {
      saveStoryboardState(persistKey, {
        storyboardId,
        scenes: storyboardScenes,
        sceneJobs,
        awaitingNextScene,
        allScenesGenerated,
        provider,
        aspectRatio,
      });
    }
  }, [
    storyboardId,
    storyboardScenes,
    sceneJobs,
    awaitingNextScene,
    allScenesGenerated,
    provider,
    aspectRatio,
  ]);

  // Effect timing'e güvenmeden senkron LS yazımı için. pollSceneJob/pollStoryboard
  // gibi async callback'ler completion noktasında bunu çağırarak son state'i
  // garanti altına alır (aksi halde React effect fire etmeden component unmount
  // olabilir ve "completed" snapshot'ı kaybolabilir).
  const persistStoryboardNow = useCallback((patch = {}) => {
    const next = { ...storyboardSnapshotRef.current, ...patch };
    storyboardSnapshotRef.current = next;
    // Sabitlenmiş key'e yaz (bkz. Save effect). Ref kullanıldığı için bu callback
    // sahne değişiminde yeniden oluşmaz — poll zinciri de stabil kalır.
    const persistKey = storyboardKeyRef.current;
    if (persistKey && next.storyboardId && (next.storyboardScenes || []).length > 0) {
      saveStoryboardState(persistKey, {
        storyboardId: next.storyboardId,
        scenes: next.storyboardScenes,
        sceneJobs: next.sceneJobs,
        awaitingNextScene: next.awaitingNextScene,
        allScenesGenerated: next.allScenesGenerated,
        // Save effect snapshot'ı provider/aspectRatio ile doldurur; burada da
        // yazalım ki completion noktasındaki senkron yazım bunları düşürmesin.
        provider: next.provider,
        aspectRatio: next.aspectRatio,
      });
    }
  }, []);

  const pollSceneJob = useCallback(
    async (sceneIndex, targetJobId, attempt = 0, gen) => {
      if (!targetJobId || attempt >= MAX_POLL_ATTEMPTS) return;
      if (gen !== storyboardGenRef.current) return; // eski nesil — dur
      try {
        const res = await fetch(`${apiUrl}api/course-video/job/${targetJobId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
          signal: storyboardAbortControllerRef.current?.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (gen !== storyboardGenRef.current) return; // fetch bittikten sonra tekrar kontrol
        const data = await res.json();
        const status = data?.status || "processing";
        const progress = data?.progress ?? 0;
        const videoUrl = data?.videoUrl || null;
        // GET job/:jobId URL'i `result.videoUrl` içinde döner (top-level videoUrl
        // yok). Yeniden üretimde (ör. yeni başlangıç görseli) storyboard polling'i
        // durmuş olabilir; sahne kartı yeni URL'i yalnızca buradan alır — aksi
        // halde "oluşturuldu" görünür ama eski video oynamaya devam eder.
        const sceneVideoUrl = videoUrl || data?.result?.videoUrl || null;

        const newJobEntry = { jobId: targetJobId, status, progress };
        const nextSceneJobs = {
          ...storyboardSnapshotRef.current.sceneJobs,
          [sceneIndex]: newJobEntry,
        };
        setSceneJobs((prev) => ({ ...prev, [sceneIndex]: newJobEntry }));
        // useEffect-tabanlı LS yazımının fire etmesini beklemeden senkron persist;
        // handleAddToScene Redux store'a yazıp re-render tetiklediğinde bile
        // "completed" snapshot'ı LS'e yazılmış olur.
        persistStoryboardNow({ sceneJobs: nextSceneJobs });

        // Update scene with video URL if completed
        if (status === "completed" && sceneVideoUrl) {
          const applyUrl = (sc) =>
            sc.index === sceneIndex ? { ...sc, videoUrl: sceneVideoUrl, status: "completed" } : sc;
          const nextScenes = storyboardSnapshotRef.current.storyboardScenes.map(applyUrl);
          setStoryboardScenes((prev) => prev.map(applyUrl));
          persistStoryboardNow({ storyboardScenes: nextScenes });
        }

        // Sahneye ekleme widget "Onayla" akışında (JobWidgetHost) yapılır; burada
        // yalnızca eski top-level videoUrl sözleşmesinde otomatik ekleme kalır.
        if (status === "completed" && videoUrl) {
          // Videoyu otomatik olarak hedef sahneye ekle
          handleAddToSceneRef.current?.(videoUrl, null, storyboardNavIdRef.current);
          // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
          approveJob(targetJobId);
        }

        if (status === "completed") {
          // Check if there are more scenes to generate
          setStoryboardScenes((currentScenes) => {
            const hasMorePending = currentScenes.some(
              (sc) => sc.status === "pending" || sc.status === "initializing"
            );
            const anyGenerating = currentScenes.some(
              (sc) =>
                (sc.status === "generating" || sc.status === "processing") &&
                sc.index !== sceneIndex
            );

            if (hasMorePending && !anyGenerating) {
              // Also check sceneJobs state
              setSceneJobs((currentJobs) => {
                const anyJobProcessing = Object.entries(currentJobs).some(
                  ([idx, job]) =>
                    Number(idx) !== sceneIndex &&
                    (job.status === "processing" || job.status === "generating")
                );
                if (!anyJobProcessing) {
                  setAwaitingNextScene(true);
                }
                return currentJobs;
              });
            }
            return currentScenes;
          });
          return;
        } else if (status === "failed") {
          // Stop — don't continue
          return;
        } else {
          sceneJobPollRefs.current[sceneIndex] = setTimeout(
            () => pollSceneJob(sceneIndex, targetJobId, attempt + 1, gen),
            POLL_INTERVAL_MS
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[VideoModal] Scene job poll error:", err);
        if (gen !== storyboardGenRef.current) return;
        sceneJobPollRefs.current[sceneIndex] = setTimeout(
          () => pollSceneJob(sceneIndex, targetJobId, attempt + 1, gen),
          POLL_INTERVAL_MS * 2
        );
      }
    },
    [apiUrl, persistStoryboardNow]
  );

  const pollStoryboard = useCallback(
    async (sbId, attempt = 0, gen) => {
      if (!sbId || attempt >= MAX_POLL_ATTEMPTS) return;
      if (gen !== storyboardGenRef.current) return; // eski nesil — dur
      try {
        const res = await fetch(`${apiUrl}api/course-video/storyboard/${sbId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
          signal: storyboardAbortControllerRef.current?.signal,
        });
        if (!res.ok) {
          // 404 hatası gelirse localStorage'dan temizle
          if (res.status === 404) {
            console.log("[VideoModal] Storyboard 404 - clearing localStorage");
            removeStoryboardState(storyboardTrainingId);
            setStoryboardId(null);
            setStoryboardScenes([]);
            setSceneJobs({});
            setIsStoryboardCreating(false);
            setView("form");
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        if (gen !== storyboardGenRef.current) return; // fetch bittikten sonra tekrar kontrol
        const data = await res.json();
        const storyboard = data?.storyboard;
        const overallStatus = storyboard?.overallStatus;
        const scenes = storyboard?.scenes || data?.scenes || [];

        // navId'yi storyboard'dan al (backend'de kayıtlıysa)
        const sbNavId = storyboard?.navId || data?.navId;
        if (sbNavId) storyboardNavIdRef.current = sbNavId;

        if (overallStatus === "initializing" || !scenes.length) {
          // LLM still working — keep spinner, keep polling
          storyboardPollRef.current = setTimeout(
            () => pollStoryboard(sbId, attempt + 1, gen),
            POLL_INTERVAL_MS
          );
          return;
        }

        // Scenes are ready — transition out of creating state
        setIsStoryboardCreating(false);
        // Storyboard modalda tüketildi → widget'taki "Storyboard" kartını Geçmiş'e taşı.
        approveStoryboardJob("init", sbId);
        setStoryboardScenes(scenes);
        // Server'dan gelen taze scenes'i hemen LS'e yaz; useEffect beklemeye gerek yok
        persistStoryboardNow({ storyboardScenes: scenes });

        // Reconcile sceneJobs with authoritative scene.status from storyboard.
        // Modal kapalıyken background widget bir job'ı tamamlarsa, modal'ın
        // sceneJobs state'i (LS'den restore) "processing" olarak kalır ve
        // render'daki `job.status || scene.status` öncelik kuralı yüzünden
        // tamamlanmış sahne hâlâ işleniyor görünür. Storyboard authoritative.
        const prevJobs = storyboardSnapshotRef.current.sceneJobs || {};
        let jobsChanged = false;
        const reconciledJobs = { ...prevJobs };
        scenes.forEach((sc) => {
          const isTerminal = sc.status === "completed" || sc.status === "failed";
          const stale = reconciledJobs[sc.index];
          if (isTerminal && stale && stale.status !== sc.status) {
            reconciledJobs[sc.index] = { ...stale, status: sc.status, progress: 100 };
            jobsChanged = true;
          }
        });
        if (jobsChanged) {
          setSceneJobs(reconciledJobs);
          persistStoryboardNow({ sceneJobs: reconciledJobs });
        }

        // Auto-start polling for any scene that is actively being generated/processed
        scenes.forEach((scene) => {
          const isActive =
            (scene.status === "generating" || scene.status === "processing") &&
            scene.jobId &&
            !startedSceneJobsRef.current.has(scene.index);
          if (isActive) {
            startedSceneJobsRef.current.add(scene.index);
            setSceneJobs((prev) => ({
              ...prev,
              [scene.index]: { jobId: scene.jobId, status: scene.status, progress: 0 },
            }));
            if (!sceneJobPollRefs.current[scene.index]) {
              sceneJobPollRefs.current[scene.index] = setTimeout(
                () => pollSceneJob(scene.index, scene.jobId, 0, gen),
                POLL_INTERVAL_MS
              );
            }
          }
        });

        const allDone = scenes.every((s) => s.status === "completed" || s.status === "failed");

        if (allDone) {
          // "Tümünü üret" job'u storyboard'a işlendi → widget kartını Geçmiş'e taşı.
          approveStoryboardJob("all", sbId);
          // All scenes completed/failed, hide buttons
          setAllScenesGenerated(true);
          setAwaitingNextScene(false);
          // En kritik nokta: tüm sahneler bittiğinde polling duruyor; final state
          // mutlaka LS'e yazılmalı ki yenileme/modal-açma sonrası "completed"
          // olarak görünsün.
          persistStoryboardNow({
            storyboardScenes: scenes,
            allScenesGenerated: true,
            awaitingNextScene: false,
          });
        } else {
          // Check if any scene is completed and there are pending scenes
          const hasCompleted = scenes.some((s) => s.status === "completed");
          const hasPending = scenes.some(
            (s) => s.status === "pending" || s.status === "initializing"
          );
          const anyGenerating = scenes.some(
            (s) => s.status === "generating" || s.status === "processing"
          );

          // Show continue buttons if we have completed scenes and pending scenes, but nothing currently generating
          if (hasCompleted && hasPending && !anyGenerating) {
            // Also check sceneJobs to make sure nothing is currently being processed
            setSceneJobs((currentJobs) => {
              const anyJobProcessing = Object.values(currentJobs).some(
                (job) => job.status === "processing" || job.status === "generating"
              );
              if (!anyJobProcessing) {
                setAwaitingNextScene(true);
              }
              return currentJobs;
            });
          }

          // Continue polling
          storyboardPollRef.current = setTimeout(
            () => pollStoryboard(sbId, attempt + 1, gen),
            POLL_INTERVAL_MS
          );
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[VideoModal] Storyboard poll error:", err);
        if (gen !== storyboardGenRef.current) return;
        storyboardPollRef.current = setTimeout(
          () => pollStoryboard(sbId, attempt + 1, gen),
          POLL_INTERVAL_MS * 2
        );
      }
    },
    [apiUrl, pollSceneJob, persistStoryboardNow]
  );

  // ── Storyboard scene edit / regenerate ────────────────────────────────────────

  const handleEditSceneStart = useCallback((idx, currentPrompt) => {
    setEditingScene(idx);
    setEditPromptValue(currentPrompt || "");
  }, []);

  const handleEditSceneCancel = useCallback(() => {
    setEditingScene(null);
    setEditPromptValue("");
  }, []);

  const handleEditSceneSave = useCallback(
    async (idx) => {
      if (!storyboardId) return;
      setIsSavingScene(true);
      try {
        const res = await fetch(
          `${apiUrl}api/course-video/storyboard/${storyboardId}/scene/${idx}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify({ videoPrompt: editPromptValue }),
          }
        );
        if (!res.ok) {
          if (res.status === 404) {
            console.log("[VideoModal] Storyboard 404 on edit - clearing localStorage");
            removeStoryboardState(storyboardTrainingId);
            setStoryboardId(null);
            setStoryboardScenes([]);
            setSceneJobs({});
            setIsStoryboardCreating(false);
            setView("form");
            return;
          }
          // Parse error response for validation errors
          let errorMessage = `HTTP ${res.status}`;
          try {
            const errorData = await res.json();
            errorMessage = localizeErrorMessage(errorData.message) || errorMessage;
          } catch (e) {
            // If JSON parsing fails, use status text
          }
          const error = new Error(errorMessage);
          error.response = { data: { message: errorMessage } };
          throw error;
        }
        setStoryboardScenes((prev) =>
          prev.map((s) =>
            s.index === idx
              ? { ...s, videoPrompt: editPromptValue, videoPromptDisplay: editPromptValue }
              : s
          )
        );
        setEditingScene(null);
        setEditPromptValue("");
      } catch (err) {
        console.error("[VideoModal] Edit scene error:", err);
        throw err; // Re-throw to be caught by VideoStoryboardPanel
      } finally {
        setIsSavingScene(false);
      }
    },
    [apiUrl, storyboardId, editPromptValue]
  );

  const handleUpdateScene = useCallback((idx, updates) => {
    setStoryboardScenes((prev) => prev.map((s) => (s.index === idx ? { ...s, ...updates } : s)));
  }, []);

  const handleCancelSceneJob = useCallback(
    async (idx) => {
      const targetJobId = sceneJobs[idx]?.jobId;
      if (!targetJobId) return;

      // Polling'i hemen durdur ki iptalden sonra processing state'ine geri düşmesin
      if (sceneJobPollRefs.current[idx]) {
        clearTimeout(sceneJobPollRefs.current[idx]);
        delete sceneJobPollRefs.current[idx];
      }

      try {
        const res = await fetch(`${apiUrl}api/course-video/job/${targetJobId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data?.success || data?.status !== "failed") {
          throw new Error("Cancel response invalid");
        }

        setSceneJobs((prev) => ({
          ...prev,
          [idx]: {
            ...(prev[idx] || {}),
            jobId: targetJobId,
            status: "failed",
            error: data?.message || "Job cancelled",
          },
        }));
        setStoryboardScenes((prev) =>
          prev.map((sc) =>
            sc.index === idx
              ? { ...sc, status: "failed", error: data?.message || "Job cancelled" }
              : sc
          )
        );
        startedSceneJobsRef.current.delete(idx);
      } catch (err) {
        console.error("[VideoModal] Cancel scene job error:", err);
      }
    },
    [apiUrl, sceneJobs]
  );

  const handleRegenerateScene = useCallback(
    async (idx, referenceImageUrl) => {
      if (!storyboardId) return;
      try {
        const fetchOpts = {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
        };
        // referenceImageUrl opsiyonel — verilirse JSON body ile gönder, yoksa eski
        // boş request davranışı (sahnede set edilmiş referenceImageUrl korunur).
        if (referenceImageUrl) {
          fetchOpts.headers["Content-Type"] = "application/json";
          fetchOpts.body = JSON.stringify({ referenceImageUrl });
        }
        const res = await fetch(
          `${apiUrl}api/course-video/storyboard/${storyboardId}/scene/${idx}/regenerate`,
          fetchOpts
        );
        if (!res.ok) {
          if (res.status === 404) {
            console.log("[VideoModal] Storyboard 404 on regenerate - clearing localStorage");
            removeStoryboardState(storyboardTrainingId);
            setStoryboardId(null);
            setStoryboardScenes([]);
            setSceneJobs({});
            setIsStoryboardCreating(false);
            setView("form");
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        const newJobId = data?.jobId;
        if (!newJobId) return;
        // Yeni job başladığı için ref'ten temizle — storyboard poll tekrar yakalayabilsin
        startedSceneJobsRef.current.delete(idx);
        setSceneJobs((prev) => ({
          ...prev,
          [idx]: { jobId: newJobId, status: "processing", progress: 0 },
        }));
        if (sceneJobPollRefs.current[idx]) clearTimeout(sceneJobPollRefs.current[idx]);
        sceneJobPollRefs.current[idx] = setTimeout(
          () => pollSceneJob(idx, newJobId, 0, storyboardGenRef.current),
          POLL_INTERVAL_MS
        );
      } catch (err) {
        console.error("[VideoModal] Regenerate scene error:", err);
      }
    },
    [apiUrl, storyboardId, pollSceneJob]
  );

  // ── Generate specific scene (for Next Scene button) ────────────────────────────
  const handleGenerateSpecificScene = useCallback(
    async (idx) => {
      console.log(
        "[VideoModal] handleGenerateSpecificScene called - Using REGENERATE API for scene:",
        idx
      );
      if (!storyboardId) return;
      setAwaitingNextScene(false);
      try {
        const res = await fetch(
          `${apiUrl}api/course-video/storyboard/${storyboardId}/scene/${idx}/regenerate`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${getToken()}` },
          }
        );
        if (!res.ok) {
          if (res.status === 404) {
            console.log(
              "[VideoModal] Storyboard 404 on generate specific scene - clearing localStorage"
            );
            removeStoryboardState(storyboardTrainingId);
            setStoryboardId(null);
            setStoryboardScenes([]);
            setSceneJobs({});
            setIsStoryboardCreating(false);
            setView("form");
            setAwaitingNextScene(false);
            return;
          }
          const errData = await res.json().catch(() => ({}));
          if (handleInsufficientBalance(errData)) {
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        const newJobId = data?.jobId;
        if (!newJobId) return;
        startedSceneJobsRef.current.delete(idx);
        setSceneJobs((prev) => ({
          ...prev,
          [idx]: { jobId: newJobId, status: "processing", progress: 0 },
        }));
        if (sceneJobPollRefs.current[idx]) clearTimeout(sceneJobPollRefs.current[idx]);
        sceneJobPollRefs.current[idx] = setTimeout(
          () => pollSceneJob(idx, newJobId, 0, storyboardGenRef.current),
          POLL_INTERVAL_MS
        );
      } catch (err) {
        console.error("[VideoModal] Generate specific scene error:", err);
      }
    },
    [apiUrl, storyboardId, pollSceneJob, storyboardTrainingId, handleInsufficientBalance]
  );

  // ── Next scene (manual trigger) ───────────────────────────────────────────────
  const handleNextScene = useCallback(
    async (referenceImageUrl) => {
      console.log("[VideoModal] handleNextScene called - Using CONTINUE API");
      if (!storyboardId) return;
      setAwaitingNextScene(false);
      try {
        // referenceImageUrl sadece mode="next" ile geçerli. Verilirse o sahne
        // image-to-video ile bu görselden başlar (gemini zincirini/önceki frame'i ezer).
        const body = { storyboardId, mode: "next" };
        if (referenceImageUrl) body.referenceImageUrl = referenceImageUrl;
        const r = await fetch(`${apiUrl}api/course-video/generate-staged/continue`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          const errData = await r.json().catch(() => ({}));
          if (handleInsufficientBalance(errData)) {
            setAwaitingNextScene(true);
            return;
          }
          throw new Error(`HTTP ${r.status}`);
        }
        const continueData = await r.json();
        if (continueData?.jobId != null && continueData?.sceneIndex != null) {
          const nextIdx = continueData.sceneIndex;
          const nextJobId = continueData.jobId;
          startedSceneJobsRef.current.delete(nextIdx);
          setSceneJobs((prev) => ({
            ...prev,
            [nextIdx]: { jobId: nextJobId, status: "processing", progress: 0 },
          }));
          if (sceneJobPollRefs.current[nextIdx]) clearTimeout(sceneJobPollRefs.current[nextIdx]);
          sceneJobPollRefs.current[nextIdx] = setTimeout(
            () => pollSceneJob(nextIdx, nextJobId, 0, storyboardGenRef.current),
            POLL_INTERVAL_MS
          );
        } else {
          setAllScenesGenerated(true);
        }
      } catch (err) {
        console.error("[VideoModal] handleNextScene error:", err);
        setAwaitingNextScene(true); // Reset button if error
      }
    },
    [apiUrl, storyboardId, pollSceneJob, handleInsufficientBalance]
  );

  // ── Generate All Remaining Scenes ──────────────────────────────────────────────
  const handleGenerateAll = useCallback(async () => {
    if (!storyboardId) return;
    setAwaitingNextScene(false);
    try {
      const r = await fetch(`${apiUrl}api/course-video/generate-staged/continue`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ storyboardId, mode: "all-remaining" }),
      });
      if (!r.ok) {
        const errData = await r.json().catch(() => ({}));
        if (handleInsufficientBalance(errData)) {
          setAwaitingNextScene(true);
          return;
        }
        throw new Error(`HTTP ${r.status}`);
      }
      const continueData = await r.json();
      // Backend tek bir staged-video-generate-all job'u döner ({ jobId, pendingCount });
      // sahneler storyboard polling'iyle gelir, job tüm sahneler bitince approve edilir.
      rememberStoryboardJob("all", storyboardId, continueData?.jobId);
      if (continueData?.jobs && Array.isArray(continueData.jobs)) {
        // Update all scenes that started generating
        continueData.jobs.forEach(({ sceneIndex, jobId }) => {
          startedSceneJobsRef.current.delete(sceneIndex);
          setSceneJobs((prev) => ({
            ...prev,
            [sceneIndex]: { jobId, status: "processing", progress: 0 },
          }));
          if (sceneJobPollRefs.current[sceneIndex])
            clearTimeout(sceneJobPollRefs.current[sceneIndex]);
          sceneJobPollRefs.current[sceneIndex] = setTimeout(
            () => pollSceneJob(sceneIndex, jobId, 0, storyboardGenRef.current),
            POLL_INTERVAL_MS
          );
        });
      }
    } catch (err) {
      console.error("[VideoModal] handleGenerateAll error:", err);
      setAwaitingNextScene(true); // Reset button if error
    }
  }, [apiUrl, storyboardId, pollSceneJob, handleInsufficientBalance]);

  // ── Add scene (append or insert at position) ──────────────────────────────────
  const handleAddScene = useCallback(
    async ({ videoPrompt, afterScene }) => {
      if (!storyboardId) return;
      // Narration ayrı gönderilmez — backend videoPrompt metnini narration alanlarına
      // da yazar. Süre body'de yok, provider belirler (metadata 8s).
      const body = { videoPrompt: (videoPrompt || "").trim() };
      // afterScene boş/undefined → sona ekle (append). Sayıysa o (1-based) sahneden
      // sonra insert; backend aralığı 1..sceneCount olarak doğrular.
      if (afterScene !== null && afterScene !== undefined && afterScene !== "") {
        body.afterScene = Number(afterScene);
      }
      const res = await fetch(`${apiUrl}api/course-video/storyboard/${storyboardId}/scene`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        if (res.status === 404) {
          console.log("[VideoModal] Storyboard 404 on add scene - clearing localStorage");
          removeStoryboardState(storyboardTrainingId);
          setStoryboardId(null);
          setStoryboardScenes([]);
          setSceneJobs({});
          setIsStoryboardCreating(false);
          setView("form");
          return;
        }
        const errData = await res.json().catch(() => ({}));
        if (handleInsufficientBalance(errData)) return;
        const msg = localizeErrorMessage(errData.message) || `HTTP ${res.status}`;
        const error = new Error(msg);
        error.response = { data: { message: msg } };
        throw error;
      }
      // Insert sonrası sonraki sahnelerin index + startTime/endTime yeniden hesaplanır.
      // Authoritative liste için storyboard'ı yeniden çekip taze scenes'i yaz.
      setAllScenesGenerated(false);
      if (storyboardPollRef.current) clearTimeout(storyboardPollRef.current);
      pollStoryboard(storyboardId, 0, storyboardGenRef.current);
    },
    [apiUrl, storyboardId, storyboardTrainingId, pollStoryboard, handleInsufficientBalance]
  );

  // ── Clear storyboard ──────────────────────────────────────────────────────────
  const handleClearStoryboard = useCallback(() => {
    stopStoryboardPolling();
    startedSceneJobsRef.current = new Set();

    // Remove from localStorage — sabit key'i sil (yüklü storyboard hangi sahnede
    // oluştuysa oranın key'i), canlı seçim değil.
    removeStoryboardState(storyboardKeyRef.current || storyboardTrainingId);
    storyboardKeyRef.current = null;

    setStoryboardId(null);
    setStoryboardScenes([]);
    setSceneJobs({});
    setIsStoryboardCreating(false);
    setEditingScene(null);
    setEditPromptValue("");
    setAwaitingNextScene(false);
    setAllScenesGenerated(false);
    setView("form");
  }, [stopStoryboardPolling, storyboardTrainingId]);

  // ── Back to form ──────────────────────────────────────────────────────────────
  const handleBackToForm = useCallback(() => {
    // Don't clear localStorage, just go back to form
    setView("form");
  }, []);

  // ── Generate ──────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (promptMode === "write") {
      if (!scenes.some((s) => s.text.trim())) {
        setError(i18n.t("FileModal.APIProviders.VideoGeneration.NarrationTextRequired"));
        return;
      }
    } else if (!narrationText.trim()) {
      setError(i18n.t("FileModal.APIProviders.VideoGeneration.NarrationTextRequired"));
      return;
    }

    // Check if there's an existing storyboard
    const existingStoryboard = loadStoryboardState(storyboardTrainingId);
    if (existingStoryboard && existingStoryboard.storyboardId) {
      setShowClearConfirmModal(true);
      return;
    }

    proceedWithGeneration();
  };

  const proceedWithGeneration = async () => {
    setShowClearConfirmModal(false);

    // Bu storyboard'u üretimin başlatıldığı sahnenin key'ine sabitle; sonraki
    // sahne geçişlerinde bile persist hep bu key'e yazsın.
    const generationKey = storyboardTrainingId;

    setIsSubmitting(true);
    setError(null);
    stopPolling();
    stopStoryboardPolling();

    // Clear old storyboard from localStorage before starting new one
    removeStoryboardState(generationKey);
    storyboardKeyRef.current = null;

    setStoryboardId(null);
    setStoryboardScenes([]);
    setSceneJobs({});
    setIsStoryboardCreating(true);
    setAwaitingNextScene(false);
    setAllScenesGenerated(false);

    const urlParams = new URLSearchParams(window.location.search);
    const resolvedTrainingId = trainingId || urlParams.get("id") || null;
    const resolvedIdealTrainingId = idealStudioTrainingId || window.idealStudioTrainingId || null;

    const isCustom = videoStyle === CUSTOM_STYLE_ID;
    const resolvedStyle = isCustom
      ? customStyleDesc?.trim() || undefined
      : videoStyle?.trim() || undefined;

    const resolvedNavId =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViewSelected
        : navId || navItemSelected || null;

    const isCustomScenes = promptMode === "write";

    const body = {
      contentLanguage,
      provider,
      aspectRatio,
      trainingId: resolvedTrainingId,
      idealStudioTrainingId: resolvedIdealTrainingId,
      projectId: window.idealStudioProjectId || null,
      organizationId: window.idealStudioOrganizationId || null,
      navId: resolvedNavId,
      sceneIndex: sceneIndex != null ? String(sceneIndex) : null,
    };

    if (isCustomScenes) {
      body.scenes = scenes
        .filter((s) => s.text.trim())
        .map((s) => {
          const scene = { text: s.text.trim(), duration: 8 };
          return scene;
        });
      body.generateVideoPrompts = true;
      body.mergeWithAudio = true;
    } else {
      body.narrationText = narrationText.trim();
    }

    storyboardNavIdRef.current = resolvedNavId;
    if (resolvedStyle && !styleReferenceFile) body.style = resolvedStyle;
    if (durationMode === "custom" && videoDuration && !isCustomScenes)
      body.duration = Number(videoDuration);

    // Process textOverlays: separate files from data
    const overlayFiles = [];
    // Skip half-filled rows (empty text / image with neither file nor URL) —
    // the backend rejects the whole request with 400 if any are sent.
    const filledOverlays = textOverlays.filter((overlay) =>
      overlay.type === "image"
        ? !!overlay.imageFile || !!overlay.imageUrl?.trim()
        : !!overlay.text?.trim()
    );
    const processedOverlays = filledOverlays.map((overlay) => {
      if (overlay.type === "image" && overlay.imageFile) {
        overlayFiles.push(overlay.imageFile);
        const { id, imageFile, imageUrl, ...rest } = overlay;
        return rest;
      }
      const { id, imageFile, ...rest } = overlay;
      return rest;
    });

    if (processedOverlays.length > 0) body.textOverlays = processedOverlays;

    // Debug: Log overlay values sent to backend
    if (processedOverlays.length > 0) {
      console.log(
        "[VideoModal] Sending overlays to backend (direct values - no frontend scaling):",
        processedOverlays
      );
    }

    const needsFormData = styleReferenceFile || overlayFiles.length > 0;

    let fetchOptions;
    if (needsFormData) {
      const formData = new FormData();
      Object.entries(body).forEach(([k, v]) => {
        if (v != null) {
          if (k === "textOverlays" || k === "scenes") {
            formData.append(k, JSON.stringify(v));
          } else {
            formData.append(k, v);
          }
        }
      });
      if (styleReferenceFile) formData.append("styleReferenceImage", styleReferenceFile);
      // Add all overlay images with the same field name "overlayImage"
      overlayFiles.forEach((file) => {
        formData.append("overlayImage", file);
      });
      fetchOptions = {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      };
    } else {
      fetchOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(body),
      };
    }

    try {
      const apiEndpoint = isCustomScenes
        ? `${apiUrl}api/course-video/generate-custom`
        : `${apiUrl}api/course-video/generate-staged`;
      const response = await fetch(apiEndpoint, fetchOptions);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (handleInsufficientBalance(errData)) {
          setIsStoryboardCreating(false);
          setView("form");
          return;
        }
        const localizedMsg = localizeErrorMessage(errData?.message);
        throw new Error(localizedMsg || `HTTP ${response.status}`);
      }

      const result = await response.json();
      const newStoryboardId = result?.storyboardId;
      if (!newStoryboardId) throw new Error("No storyboardId in response");
      rememberStoryboardJob("init", newStoryboardId, result?.initJobId);

      storyboardKeyRef.current = generationKey;
      setStoryboardId(newStoryboardId);
      startedSceneJobsRef.current = new Set();
      setView("storyboard"); // API başarıyla döndü, şimdi ikinci ekrana geç
      // isStoryboardCreating stays true — pollStoryboard will set it false when scenes are ready

      const gen = storyboardGenRef.current;
      storyboardPollRef.current = setTimeout(
        () => pollStoryboard(newStoryboardId, 0, gen),
        2000 // poll sooner for LLM completion check
      );
    } catch (err) {
      console.error("[VideoModal] Generate error:", err);
      setError(err.message || "Failed to start generation.");
      setIsStoryboardCreating(false);
      setView("form");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Insert video ──────────────────────────────────────────────────────────────
  const handleInsertVideo = () => {
    const videoUrl = jobResult?.finalVideoUrl || jobResult?.videoUrl;
    if (!videoUrl) return;
    if (typeof onVideoReady === "function") onVideoReady(videoUrl, jobResult);
    // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
    approveJob(jobId);
    if (jobId) removeJob(jobId);
    refreshJobsIndex();
    stopPolling();
    setJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setJobResult(null);
    activeJobIdRef.current = null;
    onClose();
  };

  const isProcessing =
    !!jobId && (jobStatus === "processing" || jobStatus === "pending" || isSubmitting);
  const isCompleted = jobStatus === "completed" && !!jobResult;
  const isFailed = jobStatus === "failed";
  const isStoryboardActive = isStoryboardCreating || storyboardScenes.length > 0;
  const hasContent =
    promptMode === "write"
      ? scenes.some((s) => s.text.trim().length > 0)
      : narrationText.trim().length > 0;
  const canGenerate = !isProcessing && !isSubmitting && hasContent;

  const handleUploadFile = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type. Some containers (notably .avi/.mkv/.wmv) have no
    // OS-registered MIME type, so File.type comes back empty — fall back to the
    // extension so these still upload (backend accepts any type).
    const isVideo =
      selectedFile.type.startsWith("video/") ||
      /\.(mp4|webm|mov|avi|mkv|m4v|flv|wmv|mpg|mpeg|3gp|ogv|ts|mts)$/i.test(
        selectedFile.name || ""
      );
    if (!isVideo) {
      setUploadError(true);
      setUploadErrorMessage(
        i18n.t("FileModal.APIProviders.VideoGeneration.ErrorInvalidType") ||
          "Only MP4, MOV, or WEBM files can be uploaded."
      );
      return;
    }

    // Üst sınır paylaşılan common/upload_limits.es6'dan geliyor ve tip duyarlı:
    // video için MAX_VIDEO_UPLOAD_MB (500), diğerleri için MAX_UPLOAD_MB (100).
    // Backend aynası: IdealStudioTrainingService.MAX_VIDEO_FILE_SIZE_BYTES.
    // Mesaj artık sabit "100MB" değil — fileTooLargeMessage dosyanın gerçek
    // sınırını yazıyor, yoksa 500 MB'lık bir videoda kullanıcıya yanlış sayı
    // gösteriliyordu.
    if (isFileTooLarge(selectedFile)) {
      setUploadError(true);
      setUploadErrorMessage(fileTooLargeMessage(selectedFile));
      return;
    }

    setUploadFile(selectedFile);
    setUploadError(false);
    setUploadErrorMessage("");

    // Preview only for containers the browser can render. Non-playable ones
    // (AVI/MKV/WMV/…) are transcoded to MP4 server-side on upload; a local
    // object-URL preview would just show a blank <video>.
    const playable =
      /\.(mp4|webm|ogg|ogv|m4v|mov)$/i.test(selectedFile.name || "") ||
      /^video\/(mp4|webm|ogg|quicktime)$/i.test(selectedFile.type || "");
    setUploadPreview(playable ? URL.createObjectURL(selectedFile) : null);
  };

  const handleUploadSelect = (event) => {
    const file = event.target.files?.[0];
    handleUploadFile(file);
  };

  const handleUploadDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    handleUploadFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUploadSubmit = () => {
    if (!uploadFile || !uploadFunction) return;

    setUploadUploading(true);
    setUploadError(false);
    setUploadErrorMessage("");
    setUploadStatusMessage("");

    uploadFunction(
      uploadFile,
      "",
      (url, diagnostics) => {
        setUploadUploading(false);
        // uploadFunction's error path calls back with (undefined, diagnostics)
        // — must be handled here, not treated as success (this previously
        // showed the "uploaded" checkmark even when the upload had failed).
        if (url) {
          setUploadUploaded(true);
          if (handleAddToScene) {
            handleAddToScene(url, uploadFile.name);
          }
        } else {
          setUploadError(true);
          setUploadErrorMessage(
            (diagnostics && diagnostics.message) || i18n.t("FileModal.APIProviders.error")
          );
        }
      },
      (percent) => {
        // "%NN" = numeric file-transfer progress; anything else (e.g.
        // "Sunucuda işleniyor...") is a free-text status once the transfer
        // itself finished and the backend job is running.
        if (typeof percent === "string" && percent.startsWith("%")) {
          setUploadPercentage(percent.slice(1));
        } else {
          setUploadStatusMessage(percent);
        }
      }
    );
  };

  const handleUploadClear = () => {
    setUploadFile(null);
    setUploadPreview(null);
    setUploadUploading(false);
    setUploadUploaded(false);
    setUploadError(false);
    setUploadErrorMessage("");
    setUploadPercentage(0);
    setUploadStatusMessage("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDurationModeChange = (mode) => {
    setDurationMode(mode);
    if (mode === "auto") {
      setVideoDuration("");
    }
  };

  const handlePromptModeChange = (mode) => {
    setPromptMode(mode);
    // Prompt yaz modunda süre seçimi sadece otomatik olabilir
    if (mode === "write") {
      setDurationMode("auto");
      setVideoDuration("");
    }
  };

  const handleStyleReferenceChange = (file) => {
    setStyleReferenceFile(file);
    // Style reference seçildiğinde diğer stil seçimini temizle
    if (file) {
      setVideoStyle("");
    }
  };

  const handleVideoStyleChange = (styleId) => {
    setVideoStyle(styleId);
    // Bir stil seçildiğinde style reference'ı temizle
    if (styleId) {
      setStyleReferenceFile(null);
    }
  };

  if (!open) return null;

  const resolvedTitle =
    activeTab === "director" || directorProjectId || importUrl || direct
      ? i18n.t("FileModal.APIProviders.VideoGeneration.Director.VideoEditor")
      : title || i18n.t("FileModal.APIProviders.VideoGeneration.Title");

  const titleWithTabs = (
    <div className="add-video-modal__title-with-tabs">
      <span className="add-video-modal__title-text">{resolvedTitle}</span>
      {view === "form" && (
        <div className="add-video-modal__tabs add-video-modal__tabs--inline">
          {[
            { id: "ai", label: i18n.t("FileModal.APIProviders.VideoGeneration.AIGenerate") },
            {
              id: "library",
              label: i18n.t("ChooseFromLibrary") || "Choose from library",
            },
            {
              id: "upload",
              label: i18n.t("FileModal.APIProviders.VideoGeneration.UploadMyVideo"),
            },
            {
              id: "director",
              label: i18n.t("FileModal.APIProviders.VideoGeneration.Director.VideoEditor"),
            },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`add-video-modal__tab${activeTab === tab.id ? " add-video-modal__tab--active" : ""}`}
            >
              <span className="add-video-modal__tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <AppModal
        show={open}
        onClose={onClose}
        title={titleWithTabs}
        className="addVideoModal"
        size="lg"
        maxWidth={activeTab === "director" ? "min(1800px, 96vw)" : undefined}
        dialogClassName={activeTab === "director" ? "vd-dialog" : undefined}
        bodyProps={{ className: "add-video-modal__body" }}
        headerActions={
          view === "form" ? (
            <button
              type="button"
              onClick={() => setActiveTab(activeTab === "history" ? "ai" : "history")}
              title={i18n.t("ImageHistory.Tab") || "Etkinlik Günlüğü"}
              className={`app-modal__toggle-btn${activeTab === "history" ? " app-modal__toggle-btn--active" : ""}`}
            >
              <LuHistory size={16} />
            </button>
          ) : null
        }
        footer={
          <div className="app-modal__footer">
            {view === "form" ? (
              <>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={onClose}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Close")}
                </button>
                {activeTab === "ai" && (
                  <button
                    type="button"
                    className="app-modal__btn app-modal__btn--primary"
                    disabled={!canGenerate}
                    onClick={handleGenerate}
                  >
                    {isSubmitting
                      ? i18n.t("FileModal.APIProviders.VideoGeneration.Submitting")
                      : i18n.t("FileModal.APIProviders.VideoGeneration.Generate")}
                  </button>
                )}
              </>
            ) : isStoryboardCreating ? (
              <button type="button" className="app-modal__btn app-modal__btn--primary" disabled>
                {i18n.t("FileModal.APIProviders.VideoGeneration.Submitting")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={() => setView("form")}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.BackToForm")}
                </button>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={handleClearStoryboard}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Clear")}
                </button>
                {pendingScenesCount > 0 && (
                  <>
                    <style>{`@keyframes vid-credit-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
                    <div
                      ref={creditBtnRef}
                      style={{ position: "relative", order: -1, marginRight: "auto" }}
                    >
                      <button
                        type="button"
                        onClick={handleCreditCheck}
                        disabled={creditLoading}
                        title={i18n.t("CreditEstimate.Tooltip")}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "9px 16px",
                          borderRadius: "12px",
                          border: `1px solid ${creditPopoverOpen ? "#c4b5fd" : "#dce3f3"}`,
                          background: creditPopoverOpen ? "#eef2ff" : "#f8fafc",
                          color: creditPopoverOpen ? "#7c3aed" : "#64748b",
                          fontSize: "14px",
                          fontWeight: 600,
                          cursor: creditLoading ? "wait" : "pointer",
                          transition:
                            "background 0.18s ease, color 0.18s ease, border-color 0.18s ease",
                          outline: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {creditLoading ? (
                          <LuRefreshCw
                            size={14}
                            style={{ animation: "vid-credit-spin 0.8s linear infinite" }}
                          />
                        ) : (
                          <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
                        )}
                        <span>
                          {creditLoading
                            ? i18n.t("CreditEstimate.Loading")
                            : i18n.t("CreditEstimate.Button")}
                        </span>
                      </button>

                      {creditPopoverOpen && creditInfo && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "calc(100% + 10px)",
                            left: 0,
                            background: "#ffffff",
                            borderRadius: "12px",
                            border: "1px solid #e2e8f0",
                            padding: "16px 20px",
                            minWidth: "240px",
                            boxShadow: "0 4px 24px rgba(15, 23, 42, 0.12)",
                            zIndex: 9999,
                            fontSize: "13px",
                            lineHeight: 1.6,
                          }}
                        >
                          {/* Arrow */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: -7,
                              left: 20,
                              width: 13,
                              height: 13,
                              background: "#ffffff",
                              border: "1px solid #e2e8f0",
                              borderTop: "none",
                              borderLeft: "none",
                              transform: "rotate(45deg)",
                            }}
                          />

                          <div
                            style={{
                              fontWeight: 700,
                              marginBottom: 12,
                              fontSize: "14px",
                              color: "#1e293b",
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                            }}
                          >
                            <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
                            {i18n.t("CreditEstimate.Title")}
                          </div>

                          {/* Video — kalan sahneler */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 6,
                            }}
                          >
                            <span style={{ color: "#64748b", fontSize: "13px" }}>
                              {i18n.t("FileModal.APIProviders.VideoGeneration.NextScene")}
                            </span>
                            <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "13px" }}>
                              {creditInfo.videoCost.toFixed(2)}&nbsp;
                              <span style={{ color: "#8b95af", fontWeight: 400 }}>
                                {i18n.t("ImageHistory.Credits")}
                              </span>
                            </span>
                          </div>

                          {/* Image — önizleme görseli */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 6,
                            }}
                          >
                            <span style={{ color: "#64748b", fontSize: "13px" }}>
                              {i18n.t("FileModal.APIProviders.VideoGeneration.PreviewImage")}
                            </span>
                            <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "13px" }}>
                              {creditInfo.imageCost.toFixed(2)}&nbsp;
                              <span style={{ color: "#8b95af", fontWeight: 400 }}>
                                {i18n.t("ImageHistory.Credits")}
                              </span>
                            </span>
                          </div>

                          {/* Toplam */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: 4,
                              paddingTop: 8,
                              borderTop: "1px solid #f1f5f9",
                              marginBottom: 14,
                              paddingBottom: 14,
                              borderBottom: "1px solid #f1f5f9",
                            }}
                          >
                            <span style={{ color: "#64748b", fontWeight: 600 }}>
                              {i18n.t("CreditEstimate.Cost")}
                            </span>
                            <span style={{ fontWeight: 700, color: "#1e293b" }}>
                              {creditInfo.totalRequired.toFixed(2)}&nbsp;
                              <span style={{ color: "#8b95af", fontWeight: 400 }}>
                                {i18n.t("ImageHistory.Credits")}
                              </span>
                            </span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 14,
                              paddingBottom: 14,
                              borderBottom: "1px solid #f1f5f9",
                            }}
                          >
                            <span style={{ color: "#64748b" }}>
                              {i18n.t("CreditEstimate.Balance")}
                            </span>
                            <span style={{ fontWeight: 700, color: "#1e293b" }}>
                              {creditInfo.availableCredits.toFixed(2)}&nbsp;
                              <span style={{ color: "#8b95af", fontWeight: 400 }}>
                                {i18n.t("ImageHistory.Credits")}
                              </span>
                            </span>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "7px 12px",
                              borderRadius: 8,
                              background: creditInfo.hasSufficientBalance
                                ? "rgba(124,58,237,0.08)"
                                : "rgba(239,68,68,0.08)",
                              color: creditInfo.hasSufficientBalance ? "#7c3aed" : "#ef4444",
                              fontWeight: 600,
                              fontSize: "12px",
                            }}
                          >
                            {creditInfo.hasSufficientBalance
                              ? `✓ ${i18n.t("CreditEstimate.Sufficient")}`
                              : `✗ ${i18n.t("CreditEstimate.Insufficient")}`}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {awaitingNextScene && !allScenesGenerated && (
                  <button
                    type="button"
                    className="app-modal__btn app-modal__btn--primary"
                    onClick={handleGenerateAll}
                  >
                    {i18n.t("FileModal.APIProviders.VideoGeneration.GenerateAllScenes")}
                  </button>
                )}
              </>
            )}
          </div>
        }
        footerProps={{ className: "add-video-modal__footer" }}
      >
        <div className="add-video-modal">
          <div className="add-video-modal__tab-content">
            {view === "storyboard" && (
              <VideoStoryboardPanel
                isCreating={isStoryboardCreating}
                scenes={storyboardScenes}
                sceneJobs={sceneJobs}
                editingScene={editingScene}
                editPromptValue={editPromptValue}
                onEditStart={handleEditSceneStart}
                onEditSave={handleEditSceneSave}
                isSavingScene={isSavingScene}
                onEditCancel={handleEditSceneCancel}
                onEditChange={setEditPromptValue}
                onRegenerate={handleRegenerateScene}
                onCancelJob={handleCancelSceneJob}
                onGenerateNext={handleNextScene}
                onBack={handleBackToForm}
                onClear={handleClearStoryboard}
                onAddToScene={handleAddStoryboardSceneToScene}
                onAddScene={handleAddScene}
                uploadFunction={uploadFunction}
                sceneImages={sceneImages}
                aspectRatio={aspectRatio}
                provider={provider}
                apiUrl={apiUrl}
                storyboardId={storyboardId}
                onUpdateScene={handleUpdateScene}
                onStoryboardNotFound={handleStoryboardNotFound}
                onInsufficientBalance={handleInsufficientBalance}
              />
            )}

            {view === "form" && activeTab === "ai" && (
              <div className="add-video-modal__ai-tab">
                {loadStoryboardState(storyboardTrainingId) && (
                  <div
                    className="add-video-modal__status-card"
                    style={{
                      background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)",
                      borderLeft: "4px solid #0284c7",
                      borderColor: "#bae6fd",
                      marginBottom: "20px",
                      padding: "14px 18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxShadow: "0 2px 8px rgba(2, 132, 199, 0.1)",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#0c4a6e",
                          fontWeight: "600",
                          fontSize: "14px",
                          marginBottom: "2px",
                        }}
                      >
                        {i18n.t("FileModal.APIProviders.VideoGeneration.OngoingStoryboard")}
                      </div>
                      <div style={{ color: "#0369a1", fontSize: "12px" }}>
                        {i18n.t("FileModal.APIProviders.VideoGeneration.ContinueFromWhere")}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="app-modal__btn app-modal__btn--primary"
                      onClick={() => setView("storyboard")}
                      style={{
                        padding: "8px 20px",
                        fontSize: "13px",
                        fontWeight: "600",
                        flexShrink: 0,
                        background: "#0284c7",
                        border: "none",
                      }}
                    >
                      {i18n.t("FileModal.APIProviders.VideoGeneration.ViewStoryboard")} →
                    </button>
                  </div>
                )}

                {error && (
                  <div
                    className="add-video-modal__error-alert"
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#dc2626",
                          marginBottom: "4px",
                        }}
                      >
                        {i18n.t("FileModal.APIProviders.VideoGeneration.GenerationError")}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          lineHeight: "1.5",
                          color: "#991b1b",
                        }}
                      >
                        {error}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      style={{
                        background: "transparent",
                        border: "none",
                        fontSize: "18px",
                        color: "#dc2626",
                        cursor: "pointer",
                        padding: "0",
                        lineHeight: "1",
                        flexShrink: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {isCompleted &&
                  (() => {
                    const resolvedVideoUrl = jobResult.finalVideoUrl || jobResult.videoUrl;
                    return (
                      <div className="add-video-modal__status-card add-video-modal__status-card--success">
                        <div className="add-video-modal__status-title">
                          ✓ {i18n.t("FileModal.APIProviders.VideoGeneration.VideoReady")}
                        </div>
                        {resolvedVideoUrl && (
                          <video
                            src={resolvedVideoUrl}
                            controls
                            className="add-video-modal__status-video"
                          />
                        )}
                        <div className="add-video-modal__status-actions">
                          {typeof onVideoReady === "function" && (
                            <button
                              type="button"
                              onClick={handleInsertVideo}
                              className="add-video-modal__btn add-video-modal__btn--success"
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.AddToEditor")}
                            </button>
                          )}
                          {resolvedVideoUrl && (
                            <a
                              href={resolvedVideoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="add-video-modal__btn add-video-modal__btn--secondary"
                            >
                              {i18n.t("FileModal.APIProviders.VideoGeneration.Download")}
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={onClose}
                            className="app-modal__btn app-modal__btn--ghost"
                          >
                            {i18n.t("FileModal.APIProviders.VideoGeneration.Close")}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                {isFailed && (
                  <div className="add-video-modal__status-card add-video-modal__status-card--error">
                    <div className="add-video-modal__status-title">
                      ✗ {i18n.t("FileModal.APIProviders.VideoGeneration.Failed")}
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="app-modal__btn app-modal__btn--ghost"
                    >
                      {i18n.t("FileModal.APIProviders.VideoGeneration.Close")}
                    </button>
                  </div>
                )}

                <VideoAIGenerateTab
                  provider={provider}
                  onProviderChange={setProvider}
                  aspectRatio={aspectRatio}
                  onAspectRatioChange={setAspectRatio}
                  durationMode={durationMode}
                  onDurationModeChange={handleDurationModeChange}
                  videoDuration={videoDuration}
                  onVideoDurationChange={setVideoDuration}
                  videoStyle={videoStyle}
                  onVideoStyleChange={handleVideoStyleChange}
                  videoStyles={videoStyles}
                  stylesLoading={stylesLoading}
                  narrationText={narrationText}
                  onNarrationChange={setNarrationText}
                  disabled={false}
                  customStyleDesc={customStyleDesc}
                  onCustomStyleDescChange={setCustomStyleDesc}
                  promptMode={promptMode}
                  onPromptModeChange={handlePromptModeChange}
                  styleReferenceFile={styleReferenceFile}
                  onStyleReferenceChange={handleStyleReferenceChange}
                  textOverlays={textOverlays}
                  onTextOverlaysChange={setTextOverlays}
                  navItemSelected={navItemSelected}
                  navId={navId}
                  scenes={scenes}
                  onScenesChange={setScenes}
                  enrichTexts={enrichTexts}
                  onEnrichTextsChange={setEnrichTexts}
                />
              </div>
            )}

            {view === "form" && activeTab === "library" && (
              <div className="add-video-modal__library">
                <div className="add-video-modal__library-header">
                  <div className="add-video-modal__library-search">
                    <LuSearch className="add-video-modal__library-search-icon" />
                    <input
                      type="text"
                      placeholder={i18n.t("FileModal.APIProviders.VideoGeneration.SearchLibrary")}
                      value={librarySearch}
                      onChange={(event) => setLibrarySearch(event.target.value)}
                    />
                  </div>
                </div>

                {libraryError && !libraryLoading && (
                  <div className="add-video-modal__library-status">{libraryError}</div>
                )}

                {libraryLoading && (
                  <div className="add-video-modal__library-grid">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div
                        key={index}
                        className="add-video-modal__library-card add-video-modal__library-skeleton"
                      >
                        <div className="add-video-modal__library-thumb add-video-modal__skeleton-thumb" />
                        <div className="add-video-modal__library-name add-video-modal__skeleton-name" />
                      </div>
                    ))}
                  </div>
                )}

                {!libraryLoading && !libraryError && (
                  <>
                    {libraryFiles.length === 0 ? (
                      <LibraryEmptyState onUploadClick={() => setActiveTab("upload")} />
                    ) : (
                      <LibraryGrid
                        files={libraryFiles}
                        selectedId={selectedLibraryId}
                        hoveredId={hoveredLibraryId}
                        onSelect={setSelectedLibraryId}
                        onHover={setHoveredLibraryId}
                        onAdd={(url, name) => handleAddToScene(url, name)}
                      />
                    )}
                  </>
                )}
              </div>
            )}

            {view === "form" && activeTab === "history" && (
              <HistoryTab mediaType="video" onAddToScene={handleAddToScene} />
            )}

            {view === "form" && activeTab === "upload" && (
              <VideoUploadSection
                fileInputRef={fileInputRef}
                uploadFile={uploadFile}
                uploadPreview={uploadPreview}
                uploadUploading={uploadUploading}
                uploadUploaded={uploadUploaded}
                uploadError={uploadError}
                uploadPercentage={uploadPercentage}
                uploadStatusMessage={uploadStatusMessage}
                uploadErrorMessage={uploadErrorMessage}
                onSelect={handleUploadSelect}
                onDrop={handleUploadDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onSubmit={handleUploadSubmit}
                onClear={handleUploadClear}
              />
            )}

            {view === "form" && activeTab === "director" && (
              <VideoDirector
                key={storyboardSceneId || directorProjectId}
                apiUrl={apiUrl}
                trainingId={idealStudioTrainingId || window.idealStudioTrainingId}
                navId={storyboardSceneId}
                projectId={directorProjectId}
                importUrl={importUrl}
                importPromptProfile={importPromptProfile}
                direct={direct || (directorProjectId || importUrl ? undefined : LATEST_PROJECT)}
                videoStyles={videoStyles}
                languageOptions={getLanguageOptions()}
                onInsufficientBalance={handleInsufficientBalance}
                onAddToScene={handleAddToScene}
                canReplace={!!onVideoSelected}
                audioUrl={audioUrl}
                sceneNarrationText={sceneNarrationText}
                uploadFunction={uploadFunction}
                onOpenLibrary={() => setActiveTab("library")}
              />
            )}
          </div>
        </div>
      </AppModal>

      {/* Confirmation Modal for clearing existing storyboard */}
      {showClearConfirmModal && (
        <AppModal
          show={showClearConfirmModal}
          onClose={() => setShowClearConfirmModal(false)}
          title={i18n.t("warning_title")}
          size="sm"
          footer={
            <div className="app-modal__footer">
              <button
                type="button"
                className="app-modal__btn app-modal__btn--ghost"
                onClick={() => setShowClearConfirmModal(false)}
              >
                {i18n.t("FileModal.APIProviders.VideoGeneration.Cancel")}
              </button>
              <button
                type="button"
                className="app-modal__btn app-modal__btn--primary"
                onClick={proceedWithGeneration}
                style={{ background: "#ef4444", borderColor: "#ef4444" }}
              >
                {i18n.t("FileModal.APIProviders.VideoGeneration.ProceedAnyway")}
              </button>
            </div>
          }
        >
          <div style={{ padding: "20px", textAlign: "center" }}>
            <div
              style={{
                margin: "0 auto 20px",
              }}
            >
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3
              style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", marginBottom: "8px" }}
            >
              {i18n.t("FileModal.APIProviders.VideoGeneration.ExistingStoryboardTitle")}
            </h3>
            <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: "1.5", marginBottom: "0" }}>
              {i18n.t("FileModal.APIProviders.VideoGeneration.ExistingStoryboardMessage")}
            </p>
          </div>
        </AppModal>
      )}

      <InsufficientBalanceModal
        show={insufficientBalance.show}
        onClose={() => setInsufficientBalance((prev) => ({ ...prev, show: false }))}
        requestedAmount={insufficientBalance.requestedAmount}
        currentBalance={insufficientBalance.currentBalance}
        kind={insufficientBalance.kind}
        teamName={insufficientBalance.teamName}
        resetsAt={insufficientBalance.resetsAt}
      />
    </>
  );
};

VideoModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  narrationText: PropTypes.string,
  audioUrl: PropTypes.string,
  sceneNarrationText: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  idealStudioTrainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onVideoReady: PropTypes.func,
  sceneIndex: PropTypes.number,
  navId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VideoModal;
export { readJobsIndex };
